import React, { memo, useEffect, useCallback, useState } from 'react';
import { useForm, useController } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Status } from 'common/utils/constants';
import { doPostTwoHopAlignmentBeamInfo, doGetTwoHopAlignmentBeamInfo } from 'redux/actions';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import ScrollReactTable from 'components/shared/ScrollReactTable/ScrollReactTable';
import Form from 'components/shared/Form';
import classNames from 'classnames';
import './ActionTwoHopRecAlignBeamsTable.css';

const TABLE_ID = 'th_rab';
const rows = [
  { col_id: 'p1_su1_hbf', name: 'Pivot1 SU1 HBF', id: 'su1' },
  { col_id: 'p2_du2', name: 'Pivot2 DU2', id: 'du2' }
];
const defaultValues = {
  su1: {
    az: 0,
    el: 0
  },
  du2: {
    az: 0,
    el: 0
  }
};

const ActionTwoHopRecAlignBeamsTable = memo(function ActionTwoHopRecAlignBeamsTable({ data, loading }) {
  const columns = [
    {
      id: 'rec_align_beams',
      minWidth: 240,
      width: 240,
      resizable: false,
      sortable: false,
      accessor: 'name',
      Header: ({ column }) => (
        <div id={getTableId(column.id)} className="DeviceTable_Header_OuterDiv">
          <IconAndText img={null} hoverImg={null} text={'RECOMMENDED ALIGNMENT BEAMS'} reverse onClick={null} />
        </div>
      ),
      Cell: (row) => (
        <div id={getTableId(row.original.id)} className="DeviceTable_cellWrapper">
          {row.value}
        </div>
      )
    },
    {
      id: 'az_deg',
      minWidth: 80,
      resizable: false,
      sortable: false,
      Header: ({ column }) => (
        <div id={getTableId(column.id)} className="DeviceTable_Header_OuterDiv">
          <IconAndText img={null} hoverImg={null} text={'AZ (DEG)'} reverse onClick={null} />
        </div>
      ),
      Cell: (row) => (
        <div className="DeviceTable_cellWrapper">
          <IntegerInput
            name={`${row.original.id}.az`}
            aria-labelledby={`${getTableId(row.original.id)} ${getTableId(row.column.id)}`}
            disabled={loading}
          />
        </div>
      )
    },
    {
      id: 'el_deg',
      minWidth: 80,
      resizable: false,
      sortable: false,
      Header: ({ column }) => (
        <div id={getTableId(column.id)} className="DeviceTable_Header_OuterDiv" data-tip={'EL (DEG)'}>
          <IconAndText img={null} hoverImg={null} text={'EL (DEG)'} reverse onClick={null} />
        </div>
      ),
      Cell: (row) => (
        <div className="DeviceTable_cellWrapper">
          <IntegerInput
            name={`${row.original.id}.el`}
            aria-labelledby={`${getTableId(row.original.id)} ${getTableId(row.column.id)}`}
            disabled={loading}
          />
        </div>
      )
    }
  ];

  return (
    <ScrollReactTable
      showPagination={false}
      resizable={false}
      sortable={false}
      minRows={2}
      noDataText={''}
      fetching={false}
      className={'ActionTwoHopRecAlignBeamsTable Global_Table'}
      defaultPageSize={0}
      pageSize={2}
      data={data}
      columns={columns}
      withFixedColumns={true}
    />
  );
});

const ActionTwoHopRecAlignBeamsForm = memo(function ActionTwoHopRecAlignBeamsForm({
  alignmentId,
  isGetTwoHopRecBeamInfoCalled,
  setIsGetTwoHopRecBeamInfoCalled,
  setPostTwoHopRecBeamInfoError
}) {
  const dispatch = useDispatch();
  const getTwoHopAlignmentBeamInfoStatus = useSelector((state) => selectTwoHopRecBeamInfoStatus(state, alignmentId));
  const twoHopRecBeamInfo = useSelector((state) => selectTwoHopRecBeamInfo(state, alignmentId));
  const getTwoHopAlignmentBeamInfo = useCallback(
    () =>
      new Promise((resolve, reject) => {
        dispatch(doGetTwoHopAlignmentBeamInfo(alignmentId, resolve, reject));
      }),
    [dispatch, alignmentId]
  );
  const setTwoHopAlignmentBeamInfo = useCallback(
    (data) =>
      new Promise((resolve, reject) => {
        dispatch(doPostTwoHopAlignmentBeamInfo(alignmentId, data, resolve, reject));
      }).catch((error) => {
        setPostTwoHopRecBeamInfoError(error.message);
      }),
    [dispatch, setPostTwoHopRecBeamInfoError, alignmentId]
  );
  const methods = useForm({
    defaultValues,
    values: twoHopRecBeamInfo
  });
  const { reset, formState } = methods;
  const { isSubmitting, isSubmitSuccessful, isValid, isDirty } = formState;
  const disabled = isSubmitting || !isValid || !isDirty;
  const loading = isSubmitting || getTwoHopAlignmentBeamInfoStatus === Status.PENDING;

  useEffect(() => {
    if (!twoHopRecBeamInfo && !isGetTwoHopRecBeamInfoCalled) {
      setIsGetTwoHopRecBeamInfoCalled(true);
      getTwoHopAlignmentBeamInfo().catch(() => {});
    }
  }, [getTwoHopAlignmentBeamInfo, setIsGetTwoHopRecBeamInfoCalled, twoHopRecBeamInfo, isGetTwoHopRecBeamInfoCalled]);
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [isSubmitSuccessful, reset]);

  return (
    <Form
      id="ActionTwoHopRecAlignBeamsTable_Container"
      className="ActionTwoHopRecAlignBeamsTable_Container"
      methods={methods}
      onSubmit={setTwoHopAlignmentBeamInfo}
    >
      <div id="ActionTwoHopRecAlignBeamsTable_Div" className="ActionTwoHopRecAlignBeamsTable_Div">
        <ActionTwoHopRecAlignBeamsTable data={rows} loading={loading} />
      </div>
      <div id="ActionTwoHopRecAlignBeamsTable_ActionDiv" className="ActionTwoHopRecAlignBeamsTable_ActionDiv">
        <button
          id="ActionTwoHopRecAlignBeamsTable_ActionDiv_CancelButton"
          type="reset"
          className="ActionTwoHopRecAlignBeamsTable_ActionDiv_CancelButton"
          onClick={(event) => {
            event.preventDefault();
            reset();
          }}
        >
          Cancel
        </button>
        <button
          id="ActionTwoHopRecAlignBeamsTable_ActionDiv_SubmitButton"
          className={classNames({
            ActionTwoHopRecAlignBeamsTable_ActionDiv_SubmitButton: true,
            ActionTwoHopRecAlignBeamsTable_ActionDiv_SubmitButton_Disabled: disabled
          })}
          type="submit"
          disabled={disabled}
        >
          Save
        </button>
      </div>
    </Form>
  );
});

export default ActionTwoHopRecAlignBeamsForm;

function IntegerInput({ name, ...rest }) {
  const {
    field: { value: initialValue, onChange, onBlur },
    fieldState: { isDirty }
  } = useController({ name, rules: { required: true, validate: (value) => Number.isInteger(value) } });
  const [value, setValue] = useState(initialValue);
  const handleChange = useCallback(
    (event) => {
      onChange(Number.parseInt(event.target.value, 10));
      setValue(event.target.value);
    },
    [onChange]
  );
  useEffect(() => {
    if (!isDirty) {
      setValue(initialValue.toString(10));
    }
  }, [isDirty, initialValue]);

  return (
    <input
      type="number"
      name={name}
      value={value}
      className="ActionTwoHopRecAlignBeamsInput"
      onBlur={onBlur}
      onChange={handleChange}
      {...rest}
    />
  );
}

function getTableId(id) {
  return `${TABLE_ID}_${id}`;
}

function selectTwoHopRecBeamInfoStatus(state, alignmentId) {
  return state.statuses?.[`GET-TwoHopAlignmentBeamInfo-${alignmentId}`];
}

function selectTwoHopRecBeamInfo(state, alignmentId) {
  return state.ui.actionsPage?.twoHopRecBeamInfoDictionary?.[alignmentId];
}
